/*!
 * AFTR LOVE — aftr-animations.js
 * Stack : Lenis smooth scroll + GSAP 3 + ScrollTrigger
 * Chargé en defer depuis theme.liquid, après Lenis/GSAP/ScrollTrigger
 *
 * Classes utilitaires exposées :
 *   .aftr-reveal            — fade-up simple au scroll
 *   .aftr-reveal-stagger    — enfants en cascade
 *   .aftr-word-reveal       — mots qui montent (split par mot)
 *   .aftr-parallax          — parallax (data-aftr-parallax="0.3")
 *   .aftr-zoom-scroll       — zoom progressif au scroll
 *   .aftr-sticky            — élément fixé (data-sticky-top="100")
 */
(function () {
  'use strict';

  /* ── prefers-reduced-motion ─────────────────────────────── */
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Sélecteurs de tous les éléments animés */
  const ANIMATED_SELECTORS = [
    '.aftr-reveal',
    '.aftr-reveal-stagger',
    '.aftr-word-reveal',
    '.aftr-zoom-scroll',
    '[data-aftr-reveal]',
    '[data-aftr-hero-logo]',
    '[data-aftr-hero-slogan]',
    '[data-aftr-hero-cta]',
    '[data-aftr-hero-image]',
  ].join(', ');

  /* ── Fallback IntersectionObserver (sans GSAP) ──────────── */
  function initFallbackObserver() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );
    document.querySelectorAll('.aftr-reveal, .aftr-reveal-stagger > *, .aftr-word-reveal').forEach(
      (el) => observer.observe(el)
    );
  }

  /* ── Mode réduit : tout visible, aucune animation ────────── */
  if (prefersReducedMotion) {
    document.querySelectorAll(ANIMATED_SELECTORS).forEach((el) => {
      el.style.opacity = '1';
      el.style.transform = 'none';
      el.style.filter = 'none';
    });
    initFallbackObserver();
    return;
  }

  /* ── Attente GSAP + ScrollTrigger (chargés en defer) ─────── */
  function waitForDeps(callback, attempts) {
    attempts = attempts || 0;
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      callback();
    } else if (attempts < 80) {
      setTimeout(function () { waitForDeps(callback, attempts + 1); }, 75);
    } else {
      /* Timeout 6s — fallback propre */
      console.warn('[AFTR] GSAP non chargé, fallback IntersectionObserver actif.');
      initFallbackObserver();
    }
  }

  /* ══════════════════════════════════════════════════════════ */
  /* 1. LENIS — Smooth scroll premium                          */
  /* ══════════════════════════════════════════════════════════ */
  function initLenis() {
    if (typeof Lenis === 'undefined') return;

    var lenis = new Lenis({
      duration: 1.2,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    /* Synchroniser Lenis avec ScrollTrigger */
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);

    /* Exposer pour usage externe si besoin */
    window.__aftrLenis = lenis;
  }

  /* ══════════════════════════════════════════════════════════ */
  /* 2. WORD REVEAL — Mots qui surgissent (.aftr-word-reveal)  */
  /* ══════════════════════════════════════════════════════════ */
  function initWordReveal() {
    document.querySelectorAll('.aftr-word-reveal').forEach(function (el) {
      if (el.dataset.aftrSplit) return; /* éviter double init */
      el.dataset.aftrSplit = '1';

      var originalText = el.textContent.trim();
      var words = originalText.split(/\s+/);

      el.innerHTML = words
        .map(function (w) {
          return '<span class="aftr-word-outer"><span class="aftr-word-inner">' + w + '</span></span>';
        })
        .join(' ');

      var inners = el.querySelectorAll('.aftr-word-inner');
      gsap.set(inners, { yPercent: 112, opacity: 0 });

      ScrollTrigger.create({
        trigger: el,
        start: 'top 88%',
        once: true,
        onEnter: function () {
          gsap.to(inners, {
            yPercent: 0,
            opacity: 1,
            duration: 0.85,
            ease: 'power3.out',
            stagger: 0.055,
          });
        },
      });
    });
  }

  /* ══════════════════════════════════════════════════════════ */
  /* 3. REVEAL — Fade-up simple (.aftr-reveal)                 */
  /* ══════════════════════════════════════════════════════════ */
  function initReveal() {
    document.querySelectorAll('.aftr-reveal, [data-aftr-reveal]').forEach(function (el) {
      if (el.closest('[data-aftr-hero]')) return;
      gsap.set(el, { opacity: 0, y: 44 });
      ScrollTrigger.create({
        trigger: el,
        start: 'top 88%',
        once: true,
        onEnter: function () {
          gsap.to(el, { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out' });
        },
      });
    });
  }

  /* ══════════════════════════════════════════════════════════ */
  /* 4. STAGGER — Enfants en cascade (.aftr-reveal-stagger)    */
  /* ══════════════════════════════════════════════════════════ */
  function initRevealStagger() {
    document.querySelectorAll('.aftr-reveal-stagger').forEach(function (parent) {
      var children = Array.from(parent.children);
      if (!children.length) return;
      gsap.set(children, { opacity: 0, y: 52 });
      ScrollTrigger.create({
        trigger: parent,
        start: 'top 85%',
        once: true,
        onEnter: function () {
          gsap.to(children, {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: 'power3.out',
            stagger: 0.12,
          });
        },
      });
    });
  }

  /* ══════════════════════════════════════════════════════════ */
  /* 5. PARALLAX (.aftr-parallax / data-aftr-parallax="0.3")  */
  /* ══════════════════════════════════════════════════════════ */
  function initParallax() {
    document.querySelectorAll('.aftr-parallax, [data-aftr-parallax]').forEach(function (el) {
      var speed = parseFloat(el.dataset.aftrParallax || el.dataset.speed || '0.3');
      gsap.to(el, {
        yPercent: -28 * speed,
        ease: 'none',
        scrollTrigger: {
          trigger: el.parentElement || el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });
    });
  }

  /* ══════════════════════════════════════════════════════════ */
  /* 6. ZOOM SCROLL (.aftr-zoom-scroll)                       */
  /* ══════════════════════════════════════════════════════════ */
  function initZoomScroll() {
    document.querySelectorAll('.aftr-zoom-scroll').forEach(function (el) {
      var wrapper = el.closest('.aftr-zoom-scroll-wrap') || el.parentElement || el;
      gsap.fromTo(
        el,
        { scale: 1 },
        {
          scale: 1.1,
          ease: 'none',
          scrollTrigger: {
            trigger: wrapper,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        }
      );
    });
  }

  /* ══════════════════════════════════════════════════════════ */
  /* 7. STICKY — Élément fixé dans son parent (.aftr-sticky)  */
  /* ══════════════════════════════════════════════════════════ */
  function initSticky() {
    if (window.innerWidth < 768) return; /* désactivé mobile */
    document.querySelectorAll('.aftr-sticky').forEach(function (el) {
      var topOffset = parseInt(el.dataset.stickyTop || '100', 10);
      ScrollTrigger.create({
        trigger: el,
        start: 'top ' + topOffset + 'px',
        endTrigger: el.parentElement,
        end: 'bottom bottom',
        pin: true,
        pinSpacing: false,
      });
    });
  }

  /* ══════════════════════════════════════════════════════════ */
  /* 8. HERO — Entrée orchestrée au chargement                */
  /* ══════════════════════════════════════════════════════════ */
  function initHeroEntrance() {
    var logo    = document.querySelector('[data-aftr-hero-logo]');
    var slogan  = document.querySelector('[data-aftr-hero-slogan]');
    var cta     = document.querySelector('[data-aftr-hero-cta]');
    var elements = [logo, slogan, cta].filter(Boolean);
    if (!elements.length) return;

    gsap.set(elements, { opacity: 0, y: 32 });
    gsap.to(elements, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: 'power3.out',
      stagger: 0.18,
      delay: 0.4,
    });
  }

  /* ══════════════════════════════════════════════════════════ */
  /* 9. HERO — Product reveal (flou → net au scroll)          */
  /* ══════════════════════════════════════════════════════════ */
  function initProductReveal() {
    var heroImage   = document.querySelector('[data-aftr-hero-image]');
    var heroContent = document.querySelector('[data-aftr-hero-content]');
    if (!heroImage) return;

    gsap.set(heroImage, { scale: 0.72, filter: 'blur(18px)', opacity: 0.45 });
    gsap.to(heroImage, {
      scale: 0.9,
      filter: 'blur(4px)',
      opacity: 0.9,
      duration: 1.4,
      ease: 'power2.out',
      delay: 0.2,
    });

    ScrollTrigger.create({
      trigger: '[data-aftr-hero]',
      start: 'top top',
      end: 'bottom top',
      scrub: 1.5,
      onUpdate: function (self) {
        var p = self.progress;
        gsap.set(heroImage, {
          scale: 0.9 + p * 0.5,
          filter: 'blur(' + (4 - p * 4) + 'px)',
          opacity: 0.9 + p * 0.1,
        });
        if (heroContent) {
          gsap.set(heroContent, { opacity: 1 - p * 2.2, y: p * -50 });
        }
      },
    });
  }

  /* ══════════════════════════════════════════════════════════ */
  /* 10. HOVER IMAGE SWITCH — recto/verso                     */
  /* ══════════════════════════════════════════════════════════ */
  function initImageSwitch() {
    document.querySelectorAll('[data-aftr-card]').forEach(function (card) {
      var primary   = card.querySelector('[data-aftr-img-primary]');
      var secondary = card.querySelector('[data-aftr-img-secondary]');
      if (!primary || !secondary) return;

      gsap.set(secondary, { opacity: 0, position: 'absolute', inset: 0 });

      card.addEventListener('mouseenter', function () {
        gsap.to(primary,   { opacity: 0, duration: 0.35, ease: 'power2.out' });
        gsap.to(secondary, { opacity: 1, duration: 0.35, ease: 'power2.out' });
      });
      card.addEventListener('mouseleave', function () {
        gsap.to(primary,   { opacity: 1, duration: 0.35, ease: 'power2.out' });
        gsap.to(secondary, { opacity: 0, duration: 0.35, ease: 'power2.out' });
      });
    });
  }

  /* ══════════════════════════════════════════════════════════ */
  /* 11. NAV scroll behavior                                  */
  /* ══════════════════════════════════════════════════════════ */
  function initNavScroll() {
    var nav = document.querySelector('.aftr-nav');
    if (!nav) return;
    window.addEventListener('scroll', function () {
      nav.classList.toggle('aftr-nav--scrolled', window.scrollY > 80);
    }, { passive: true });
  }

  /* ══════════════════════════════════════════════════════════ */
  /* INIT PRINCIPAL                                            */
  /* ══════════════════════════════════════════════════════════ */
  function init() {
    gsap.registerPlugin(ScrollTrigger);
    gsap.config({ nullTargetWarn: false });
    ScrollTrigger.config({ limitCallbacks: true });

    initLenis();         /* 1 — smooth scroll */
    initWordReveal();    /* 2 — mots qui montent */
    initReveal();        /* 3 — fade-up */
    initRevealStagger(); /* 4 — cascade */
    initParallax();      /* 5 — parallax */
    initZoomScroll();    /* 6 — zoom scroll */
    initSticky();        /* 7 — sticky */
    initHeroEntrance();  /* 8 — hero load */
    initProductReveal(); /* 9 — hero scroll */
    initImageSwitch();   /* 10 — hover card */
    initNavScroll();     /* 11 — nav */

    window.addEventListener('load', function () { ScrollTrigger.refresh(); });

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { ScrollTrigger.refresh(); }, 250);
    });
  }

  /* ── Lancement ────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { waitForDeps(init); });
  } else {
    waitForDeps(init);
  }
})();
